import { Box, type Theme } from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { BottomSheet } from "@src/appV2/redesign/components/BottomSheet";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { DialogHeader } from "@src/appV2/redesign/components/DialogHeader";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";

interface PostAsOpenShiftInfoModalProps {
  modalState: UseModalState;
  onClose: () => void;
}

/**
 * Information modal explaining the "Post as open shift if not accepted" option.
 *
 * Per spec: Opens when user clicks the info icon next to the checkbox.
 * Explains what happens when the checkbox is checked vs unchecked.
 */
export function PostAsOpenShiftInfoModal(props: PostAsOpenShiftInfoModalProps) {
  const { modalState, onClose } = props;

  const isMobile = useIsMobile();

  const handleClose = () => {
    modalState.closeModal();
    onClose();
  };

  const content = (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
      })}
    >
      <Text
        sx={(theme: Theme) => ({
          color: theme.palette.text?.primary,
        })}
        variant="body2"
      >
        <strong>When checked:</strong> If the invited professional doesn&apos;t accept the shift
        within the invite expiration time, the shift will automatically be posted to the marketplace
        as an open shift for other qualified professionals to claim.
      </Text>

      <Text
        sx={(theme: Theme) => ({
          color: theme.palette.text?.primary,
        })}
        variant="body2"
      >
        <strong>When unchecked:</strong> The shift will only be visible to the invited professional.
        If they don&apos;t accept, the shift won&apos;t be posted and you&apos;ll need to manually
        post it or invite someone else.
      </Text>

      <Text
        sx={(theme: Theme) => ({
          color: theme.palette.text?.secondary,
        })}
        variant="body2"
      >
        This option is checked by default to maximize your chances of filling the shift.
      </Text>
    </Box>
  );

  const footerContent = (
    <DialogFooter
      layout="horizontal"
      primaryAction={{
        label: "Got it",
        onClick: handleClose,
      }}
    />
  );

  if (isMobile) {
    return (
      <BottomSheet modalState={modalState}>
        <DialogHeader title="Post as open shift" onClose={handleClose} />
        <Box
          sx={(theme: Theme) => ({
            padding: theme.spacing(5),
            overflowY: "auto",
            flex: 1,
          })}
        >
          {content}
        </Box>
        <Box
          sx={(theme: Theme) => ({
            borderTop: `1px solid ${theme.palette.border?.base}`,
          })}
        >
          {footerContent}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog footerContent={footerContent} modalState={modalState} title="Post as open shift">
      {content}
    </Dialog>
  );
}
